// Plain-text export of each widget, for "Copy answer" and the widgets' own
// copy buttons. No JSX here, so the answer tests can import it in Node.

import { EMAIL_PURPOSES } from '../../../../shared/widgets.js'
import { queryItems, stepCode } from '../../../lib/curl.js'

const list = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])
const join = (...parts) => parts.flat().filter((p) => p !== null && p !== undefined && p !== false && p !== '').join('\n')

export const SEVERITY_LABELS = { blocker: 'Blocker', caution: 'Caution', tip: 'Tip' }
export const CATEGORY_LABELS = { data: 'Data', api: 'API', client: 'Client', process: 'Process', decision: 'Decision' }

/** Gotchas a host card carries (attach_to matched in buildAnswer). */
function attachedText(block) {
  const items = list(block?.gotchas)
  return items.length ? `Watch for:\n${items.map(gotchaLine).join('\n')}` : null
}

function gotchaLine(g) {
  const label = SEVERITY_LABELS[g.severity] ?? 'Note'
  return `- [${label}] ${g.title ?? ''}${g.why ? `: ${g.why}` : ''}${g.fix ? ` Do this: ${g.fix}` : ''}`
}

// ---- Shared builders, also used by the widgets ------------------------------

/** The questions grouped as the client receives them. */
export function questionnaireText(groups) {
  return list(groups)
    .map((group) => {
      const questions = list(group.questions).filter((q) => q.text)
      return questions.length ? `${group.name}\n${questions.map((q, i) => `  ${i + 1}. ${q.text}`).join('\n')}` : ''
    })
    .filter(Boolean)
    .join('\n\n')
}

/** The full email: greeting, the model's body, sign-off. Tone only changes the frame. */
export function composeEmail({ body = '', recipient, sender, tone = 'friendly' }) {
  const formal = tone === 'formal'
  const to = recipient?.trim()
  const greeting = formal ? `Dear ${to || 'Sir or Madam'},` : `Hi${to ? ` ${to}` : ''},`
  const signoff = formal ? 'Kind regards,' : 'Thanks,'
  const lines = [greeting, '', body.trim(), '', signoff]
  if (sender?.trim()) lines.push(sender.trim())
  lines.push('DCKAP')
  return lines.join('\n')
}

export function emailDraftValue(input, state) {
  return {
    subject: state?.subject ?? input.subject ?? '',
    body: state?.body ?? input.body ?? '',
    recipient: state?.recipient ?? input.recipient_name ?? '',
    sender: state?.sender ?? '',
    tone: state?.tone ?? 'friendly',
    // The whole message as the user edited it; null until they do.
    message: state?.message ?? null,
  }
}

/** The message as shown and copied: the user's edited text, else the composed email. */
export function emailMessage(value) {
  return value.message ?? composeEmail(value)
}

/** One-line summary of a picked scope, sent back as the next question. */
export function scopeMessage(pipes, columns = []) {
  const on = pipes.filter((p) => p.on && p.name?.trim())
  if (!on.length) return 'Scope: no pipes selected yet.'
  return `Scope: ${on
    .map((p) => {
      const extra = columns.filter((c) => p.values?.[c.id]?.trim()).map((c) => `${c.name || 'Note'}: ${p.values[c.id].trim()}`)
      return `${p.name.trim()} (${[p.custom ? 'custom' : null, p.direction, ...extra].filter(Boolean).join(', ')})`
    })
    .join('; ')}.`
}

// ---- Per widget ---------------------------------------------------------------

function endpointText(input, _state, block) {
  const own =
    input.api === 'odata'
      ? join(
          `${input.method ?? 'GET'} ${input.path ?? ''}  (${input.op_id ?? ''})`,
          input.table ? `OData query on ${input.table}` : null,
          list(input.columns).length ? `Columns: ${list(input.columns).join(', ')}` : null,
          input.date_column ? `Changed since: ${input.date_column}` : null,
          input.quote_filter ? `Exclude quotes: ${input.quote_filter}` : null,
          list(input.notes).map((n) => `- ${n}`),
        )
      : join(
          `${input.method ?? ''} ${input.path ?? ''}  (${input.op_id ?? ''})`,
          input.summary,
          list(input.path_params).length
            ? `Path params: ${list(input.path_params).map((p) => (p.example ? `${p.name} (e.g. ${p.example})` : p.name)).join(', ')}`
            : null,
          queryItems(input).length
            ? `Query: ${queryItems(input).map((q) => `${q.name}${q.required ? ' (required)' : ''}`).join(', ')}`
            : null,
          list(input.expand_options).length ? `Include: ${list(input.expand_options).join(', ')}` : null,
          input.body ? `Body:\n${input.body}` : null,
          list(input.response_fields).length ? `Key response fields: ${list(input.response_fields).join(', ')}` : null,
          list(input.notes).map((n) => `- ${n}`),
        )
  const alternate = block?.alternate ? endpointText(block.alternate.input ?? {}, null, block.alternate) : null
  return join(own, attachedText(block), alternate ? `\n${alternate}` : null)
}

function fieldsText(input, _state, block) {
  return join(
    `${input.title ?? 'Fields'}${input.target_system ? ` (${input.source_system} → ${input.target_system})` : ''}:\n${list(input.fields)
      .map(
        (f) =>
          `- ${f.name}${f.target ? ` → ${f.target}` : ''}${f.type ? ` (${f.type}${f.required ? ', required' : ''})` : f.required ? ' (required)' : ''}${[f.purpose, f.note].filter(Boolean).length ? `: ${[f.purpose, f.note].filter(Boolean).join(' — ')}` : ''}`,
      )
      .join('\n')}`,
    attachedText(block),
  )
}

function gotchasText(input) {
  const items = list(input.items)
  if (!items.length) return ''
  return `${input.title ?? 'Watch for'}${input.subject ? ` (${input.subject})` : ''}:\n${items.map(gotchaLine).join('\n')}`
}

function stepLines(step, i) {
  return join(
    `${i + 1}. ${step.label ?? ''} (${step.system ?? ''}) — ${step.op_id ?? ''}`,
    step.method || step.path ? `   ${[step.method, step.path].filter(Boolean).join(' ')}` : null,
    step.note ? `   ${step.note}` : null,
    step.watch ? `   Watch for: ${step.watch}` : null,
  )
}

function pipeFlowText(input, _state, block) {
  return join(
    `${input.pipe ?? 'Pipe'} pipe${input.complexity ? ` (${input.complexity})` : ''}:`,
    list(input.flows).map((flow) => join(`${flow.direction_label ?? ''}:`, list(flow.steps).map(stepLines))),
    attachedText(block),
  )
}

function handoffText(input, _state, block) {
  return join(
    `${input.title ?? 'Handoff'} (${input.source_system ?? ''} → ${input.target_system ?? ''}):`,
    list(input.steps).map((step, i) =>
      join(
        `${i + 1}. ${step.label ?? ''} (${step.system ?? ''}) — ${step.op_id ?? ''}`,
        stepCode(step)
          ? stepCode(step)
              .split('\n')
              .map((l) => `   ${l}`)
              .join('\n')
          : null,
        list(step.checks).length ? `   Check: ${list(step.checks).join(' ')}` : null,
        list(step.keep).length ? `   Keep: ${list(step.keep).join(', ')}` : null,
        step.watch ? `   Watch for: ${step.watch}` : null,
      ),
    ),
    list(input.travelling_ids).length
      ? `Ids that travel: ${list(input.travelling_ids).map((t) => `${t.from} → ${t.to}`).join('; ')}`
      : null,
    attachedText(block),
  )
}

function questionnaireTextFor(input) {
  const body = questionnaireText(input.groups)
  return body ? `${input.title ?? 'Client questionnaire'}:\n${body}` : ''
}

function checklistText(input) {
  return join(
    `${input.title ?? 'Checklist'}${input.subject ? ` (${input.subject})` : ''}:`,
    list(input.items).map(
      (i) => `- ${i.item ?? ''}${i.owner ? ` — ${i.owner}` : ''}${i.ready_when ? ` — Ready when: ${i.ready_when}` : ''}`,
    ),
  )
}

function emailText(input, state) {
  const value = emailDraftValue(input, state)
  return join(`${EMAIL_PURPOSES[input.purpose] ?? 'Email'}`, `Subject: ${value.subject}`, '', emailMessage(value))
}

function clarifyText(input) {
  return join(input.question, list(input.options).map((o) => `- ${o.label}${o.description ? `: ${o.description}` : ''}`))
}

function scopeText(input) {
  return join(
    'Which pipes are in scope?',
    list(input.pipes).map((p) => `- ${p.name} (${[p.direction, p.complexity].filter(Boolean).join(', ')})`),
  )
}

export const WIDGET_TEXT = {
  show_endpoint: endpointText,
  show_fields: fieldsText,
  show_gotchas: gotchasText,
  show_pipe_flow: pipeFlowText,
  show_handoff: handoffText,
  show_questionnaire: questionnaireTextFor,
  show_checklist: checklistText,
  show_email_draft: emailText,
  ask_clarification: clarifyText,
  pick_scope: scopeText,
}

/** Text for one widget block; `state` is the user's saved edits, if any. */
export function widgetText(block, state) {
  const fn = WIDGET_TEXT[block.kind]
  return fn ? fn(block.input ?? {}, state, block) : ''
}
